'use client'

import * as SliderPrimitive from '@radix-ui/react-slider'
import { useLocale } from 'next-intl'
import {
  ChangeEvent,
  ComponentProps,
  PropsWithChildren,
  ReactNode,
  RefObject,
  useEffect,
  useState,
} from 'react'

import { cn, Slot } from '@/shared/lib'

import {
  SliderProvider,
  type SliderValueFormat,
  useSliderContext,
} from './provider'
import { Input } from '../input'

type RootProps = {
  value?: number[]
  defaultValue?: number[]
  onValueChange?: (value: number[]) => void
  min?: number
  max?: number
  step?: number
  minStepsBetweenThumbs?: number
  valueFormat: SliderValueFormat
  className?: string
}

export const Root = ({
  className,
  children,
  ...restProps
}: PropsWithChildren<RootProps>) => {
  return (
    <SliderProvider {...restProps}>
      <div className={className}>{children}</div>
    </SliderProvider>
  )
}

type TrackProps = {
  color?: 'primary' | 'secondary'
  ref?: RefObject<HTMLSpanElement>
  className?: string
}

export const Track = ({
  ref,
  color = 'primary',
  className,
  ...restProps
}: TrackProps) => {
  const {
    selectedRange,
    updateSelectedRange,
    range,
    thumbCount,
    step,
    minStepsBetweenThumbs,
  } = useSliderContext()

  const handleValueChange = (values: number[]) => {
    updateSelectedRange(values)
  }

  return (
    <div className={cn('relative flex h-11', className)}>
      <SliderPrimitive.Root
        ref={ref}
        value={selectedRange}
        onValueChange={handleValueChange}
        className={cn(
          'relative flex w-full touch-none items-center select-none',
        )}
        min={range.min}
        max={range.max}
        step={step}
        minStepsBetweenThumbs={minStepsBetweenThumbs}
        {...restProps}
      >
        <SliderPrimitive.Track className="relative h-1 w-full grow overflow-hidden rounded-full bg-gray-200">
          <SliderPrimitive.Range
            className={cn('absolute h-full', {
              'bg-black': color === 'primary',
              'bg-purple-500': color === 'secondary',
            })}
          />
        </SliderPrimitive.Track>
        {Array.from({ length: thumbCount }).map((_, index) => (
          <SliderPrimitive.Thumb
            key={index}
            className={cn(
              'block h-6 w-6 rounded-full bg-white transition-all',
              'shadow-[0px_5.14px_11.14px_0px_rgba(0,0,0,0.12),0px_0.43px_3.43px_0px_rgba(0,0,0,0.12)]',
              'focus-visible:ring-3 focus-visible:outline-none', // FIXME: 포커스 스타일 고민
              'disabled:pointer-events-none disabled:opacity-50',
              {
                'focus-visible:ring-ring/50': color === 'primary',
                'focus-visible:ring-purple-200/50': color === 'secondary',
              },
            )}
          />
        ))}
      </SliderPrimitive.Root>
    </div>
  )
}

/** format 결과에서 숫자만 추출 (편집/포커스 시 표시용) */
const getDigitsOnly = (formatted: string) => formatted.replace(/\D/g, '')

export type RangeValueInputFieldProps = Pick<
  ComponentProps<typeof Input>,
  'size' | 'padding' | 'className'
> & {
  value: number
  onChange: (value: number) => void
  onBlur: (value: number) => void
  format: (value: number) => string
  parse: (input: string) => number
  maxLength: number
  unit: ReactNode
  ariaLabel?: string
}

const RangeValueInputField = ({
  value,
  size,
  onChange,
  onBlur,
  format,
  parse,
  maxLength,
  unit,
  ariaLabel,
  padding,
  className,
}: RangeValueInputFieldProps) => {
  const locale = useLocale()
  const isEnglish = locale === 'en'

  const formattedValue = format(value)
  const digitsOnly = getDigitsOnly(formattedValue)

  const [inputValue, setInputValue] = useState(digitsOnly)

  const [focused, setFocused] = useState(false)

  useEffect(() => {
    if (!focused) setInputValue(digitsOnly)
  }, [value, focused, digitsOnly])

  const valueToShow = focused ? inputValue : formattedValue

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const slicedValue = event.target.value
      .replace(/\D/g, '')
      .slice(0, maxLength)
    setInputValue(slicedValue)
    onChange(parse(String(slicedValue)))
  }

  const handleFocus = () => {
    setFocused(true)
  }

  const handleBlur = () => {
    setFocused(false)
    onBlur(parse(String(inputValue)))
  }

  return (
    <Input
      type="text"
      inputMode="numeric"
      size={size}
      value={valueToShow}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      maxLength={maxLength}
      aria-label={ariaLabel}
      className={className}
      padding={padding}
    >
      <Slot name={isEnglish ? 'left' : 'right'}>
        <span className="w-fit shrink-0">{unit}</span>
      </Slot>
    </Input>
  )
}

type RangeInputProps = Pick<
  ComponentProps<typeof RangeValueInputField>,
  'size' | 'padding'
> & {
  inputClassName?: string
  className?: string
}

const DEFAULT_GAP_WHEN_REVERSED = 1

export const RangeInput = ({
  size = 'default',
  padding,
  inputClassName,
  className,
}: RangeInputProps) => {
  const {
    selectedRange,
    updateInternalSelectedRange,
    updateSelectedRange,
    range,
    valueFormat,
    step = 1,
  } = useSliderContext()

  const { formatValue, parse, maxLength, unit, gapWhenReversed } = valueFormat

  const gap = gapWhenReversed ?? step ?? DEFAULT_GAP_WHEN_REVERSED

  const [selectedMin, selectedMax] = selectedRange

  const applyMin = (raw: number) => {
    // min + gap <= max 되도록 min 상한 적용 (0~0 방지)
    // raw 값이 allowed range (range.min 이상, range.max 이하)에 들어오도록 clamp
    const clampedMin = Math.max(range.min, Math.min(range.max - gap, raw))
    const newMax = Math.min(range.max, Math.max(selectedMax, clampedMin + gap))
    updateSelectedRange([clampedMin, newMax])
  }

  const applyMax = (raw: number) => {
    // min + gap <= max 되도록 max 하한 적용 (0~0 방지)
    const clampedMax = Math.min(range.max, Math.max(range.min + gap, raw))
    const newMin = Math.max(range.min, Math.min(selectedMin, clampedMax - gap))
    updateSelectedRange([newMin, clampedMax])
  }

  return (
    <div className={cn('flex items-center gap-1', className)}>
      <RangeValueInputField
        size={size}
        value={selectedMin}
        onChange={newMin => {
          updateInternalSelectedRange([newMin, selectedMax])
        }}
        onBlur={applyMin}
        format={formatValue}
        parse={parse}
        maxLength={maxLength}
        unit={unit}
        ariaLabel="최소값"
        padding={padding}
        className={inputClassName}
      />
      <span className="typo-label1 font-semibold text-black">~</span>
      <RangeValueInputField
        size={size}
        value={selectedMax}
        onChange={newMax => {
          updateInternalSelectedRange([selectedMin, newMax])
        }}
        onBlur={applyMax}
        format={formatValue}
        parse={parse}
        maxLength={maxLength}
        unit={unit}
        ariaLabel="최대값"
        padding={padding}
        className={inputClassName}
      />
    </div>
  )
}
